import { useEffect, useMemo, useRef, useState } from "react";
import { Spin } from "antd";
import {
  useCartState,
  useCartActions,
  useEnrichedProducts,
  useProductList,
  useUpdateActiveCart,
} from "../../hooks/appHooks";
import { useProductsLayout } from "../../context/ProductsLayoutContext";
import useVibrate from "../../hooks/useVibrate";
import useAvailableInFavorites from "../../hooks/useAvailableInFavorites";

import listStyles from "./ProductsList.module.css";
import gridStyles from "./ProductsListGrid.module.css";
import "./ProductsListKeyframes.css";

import {
  getProductImage,
  ProductImageDisplay,
} from "../Images/ProductImageService";
import plusIcon from "./plus.svg";
import minusIcon from "./minus.svg";
import CategoryNavigation from "./CategoryNavigation";
import SubCategoryNavigation from "./SubCategoryNavigation";
import ProductComparisonModal from "../PriceList/productComparisonModal";
import ProductComparison from "../PriceList/productComparison";
import {
  peekShelfTarget,
  consumeShelfTarget,
  onShelfTarget,
} from "../../utils/shelfTarget";
import { prefetchProductImages, whenIdle } from "../../utils/productImages";

/* -------------------------------- */
/* פונקציות עזר (אינן משתנות)      */
/* -------------------------------- */
/* כמה כרטיסים ראשונים טוענים תמונה מיד (המסך הראשון ברשימה/ברשת);
   השאר נטענים lazy כשמתקרבים אליהם בגלילה. */
const EAGER_IMAGE_COUNT = 8;
/* Page entrance (same beat as the home page): the navigation bars drop in
   and the first screenful of cards rises in a stagger. The `list__entering`
   class that drives it (ProductsList.module.css) lives this long after the
   shelf renders, so later re-renders (subcategory swipes) don't replay it. */
const ENTRANCE_MS = 1200;
const ENTRANCE_STAGGER_MAX = 15; // cards beyond this share the last delay

export const convertWeightUnit = (weightUnit) => {
  if (!weightUnit) return "";
  switch (weightUnit.toLowerCase()) {
    case "g":
      return "גרם";
    case "kg":
      return 'ק"ג';
    case "ml":
      return 'מ"ל';
    case "l":
      return "ליטר";
    case "u":
      return "יחידות";
    default:
      return weightUnit;
  }
};

// const priceFormat = (price) => price.toFixed(2);
const priceFormat = (price) => {
  if (typeof price !== "number") return "-";
  return price.toFixed(2);
};

const discountPriceFormat = ({ units, totalPrice }, styles) => (
  <div
    className={styles['list__discount-price']}
    style={{
      display: "flex",
      flexDirection: "row-reverse",
      alignItems: "center",
      color: "#ff0000",
      fontWeight: "bold",
    }}
  >
    <p style={{ marginLeft: "0.3rem" }}>{units}</p>
    <p>{"יחידות ב"}</p>
    <p>{" - "}</p>
    <p>{priceFormat(totalPrice)}</p>
    <p style={{ fontWeight: "bold" }}>{"₪"}</p>
  </div>
);

/* כפתורי הוספה/הורדה/אישור/מחיקה לסל
   ה-class `visible` קיים רק בתצוגת הרשימה — הכפתור לא מוצג ברשת,
   אז משתמשים תמיד ב-listStyles להצגה/הסתרה. */
const makeVisible = (button) => {
  if (button && listStyles.visible) button.classList.add(listStyles.visible);
};
const makeInvisible = (button) => {
  if (button && listStyles.visible) button.classList.remove(listStyles.visible);
};
const changeButtonToAddProductButton = (button) => {
  button.style.backgroundColor = "#00c200";
  makeVisible(button);
  button.innerText = "הוסף לסל";
};
const changeButtonToNoChangeAmountButton = (button) => {
  makeInvisible(button);
};
const changeButtonToUpdateAmountButton = (button) => {
  button.style.backgroundColor = "#008cba";
  makeVisible(button);
  button.innerText = "עדכן כמות";
};
const changeButtonToDeleteProductButton = (button) => {
  button.style.backgroundColor = "#ff0000";
  makeVisible(button);
  button.innerText = "הסר מהסל";
};

/* -------------------------------- */
/*       ProductsList component     */
/* -------------------------------- */
function ProductsList() {
  const { add, remove, update } = useCartActions();
  const { productsWithDetails, isLoadingProducts } = useEnrichedProducts();
  const { cart } = useCartState();
  const { sendActiveCart } = useUpdateActiveCart();
  const { layout } = useProductsLayout();
  const vibrate = useVibrate();
  /* בתצוגת רשימה משתמשים ב-listStyles בלבד (התנהגות מקורית).
     בתצוגת רשת מצרפים את ה-classes משני המודולים, כך ש-listStyles
     מספק את הבסיס וה-gridStyles מבצע override רק על המקומות
     הספציפיים לפריסת הרשת. */
  const styles = useMemo(() => {
    if (layout !== "grid") return listStyles;
    const keys = new Set([
      ...Object.keys(listStyles),
      ...Object.keys(gridStyles),
    ]);
    const merged = {};
    keys.forEach((key) => {
      const a = listStyles[key] || "";
      const b = gridStyles[key] || "";
      merged[key] = `${a} ${b}`.trim();
    });
    return merged;
  }, [layout]);
  const {
    activeCategoryIndex,
    activeSubCategoryIndex,
    allCategories,
    all_sub_categories,
    setActiveCategoryIndex,
    setActiveSubCategoryIndex,
  } = useProductList();

  const [productAmounts, setProductAmounts] = useState({});
  const [oldProductAmounts, setOldProductAmounts] = useState({});
  const [animatingProducts, setAnimatingProducts] = useState({}); // { barcode: 'up' | 'down' }

  useEffect(() => {
    sendActiveCart();
  }, [cart, sendActiveCart]); // ← מופעל רק כש-cart משתנה

  /* כאן נשמור את ה-inline style שנחיל על ה-container (כדי ליצור אנימציות) */
  const [containerStyle, setContainerStyle] = useState({});

  /* אנימציית הכניסה לעמוד — פעילה רק בשנייה הראשונה אחרי שהמדף מוצג */
  const [entering, setEntering] = useState(true);
  const enteringRef = useRef(true);
  enteringRef.current = entering;
  useEffect(() => {
    if (isLoadingProducts) return undefined;
    const timer = setTimeout(() => setEntering(false), ENTRANCE_MS);
    return () => clearTimeout(timer);
  }, [isLoadingProducts]);

  /* refs לזיהוי כיוון Swipe */
  const startTouch = useRef({ x: 0, y: 0 });
  const swipeDirection = useRef(null);
  /* קונטיינר כרטיסי המוצרים — לאיפוס הגלילה אחרי מעבר תת-קטגוריה */
  const productsContainerRef = useRef(null);

  const [selectedBarcode, setSelectedBarcode] = useState(null);
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);

  const openComparisonModal = (barcode) => {
    setSelectedBarcode(barcode);
    setIsComparisonModalOpen(true);
  };
  const closeComparisonModal = () => {
    setIsComparisonModalOpen(false);
    setSelectedBarcode(null);
  };

  /* -------------------------------- */
  /* זיהוי תחילת נגיעה והחלקה         */
  /* -------------------------------- */
  const handleTouchStart = (event) => {
    swipeDirection.current = null;
    setContainerStyle({});
    // a touch during the entrance ends it — nothing should still be
    // drifting in while the user scrolls or swipes
    if (enteringRef.current) setEntering(false);
    const touch = event.touches[0];
    startTouch.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchMove = (event) => {
    const touch = event.touches[0];
    const moveX = touch.clientX;
    const moveY = touch.clientY;
    const deltaX = moveX - startTouch.current.x;
    const deltaY = moveY - startTouch.current.y;

    const SWIPE_THRESHOLD = 80; // כמה רחוק נחשב כהחלקה

    if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
      swipeDirection.current = deltaX > 0 ? "right" : "left";
    }
  };

  /**
   * פונקציות עזר להפעלת "המעבר המשולש" (3 שלבים) שהשתמשת בהם בקטגוריות:
   * 1) middleToX
   * 2) XToY ב-1ms
   * 3) YToMiddle
   */
  const animateLeft = () => {
    // הדף זז שמאלה
    setContainerStyle({ animation: "middleToLeft 0.2s ease" });
    setTimeout(() => {
      // קפיצה לימין
      setContainerStyle({
        animation: "leftToRight 1ms steps(1) forwards",
      });
    }, 200);
    setTimeout(() => {
      // כניסה מימין לאמצע
      setContainerStyle({ animation: "rightToMiddle 0.3s ease" });
    }, 201);
  };

  const animateRight = () => {
    // הדף זז ימינה
    setContainerStyle({ animation: "middleToRight 0.2s ease" });
    setTimeout(() => {
      // קפיצה לשמאל
      setContainerStyle({
        animation: "rightToLeft 1ms steps(1) forwards",
      });
    }, 200);
    setTimeout(() => {
      // כניסה משמאל לאמצע
      setContainerStyle({ animation: "leftToMiddle 0.3s ease" });
    }, 201);
  };

  /* גלילת אזור התוכן חזרה למעלה אחרי מעבר תת-קטגוריה. בארכיטקטורת ה-grid של
     ה-app-shell ה-window/body אינם נגללים — רק .app-scroll — לכן יש לגלול אותו
     ישירות כדי שהקטגוריה ותת-הקטגוריה יחזרו להיראות בראש העמוד. */
  const scrollToTop = () => {
    // אזור הגלילה של ה-app-shell (זה שנגלל בפועל בעמוד המוצרים)
    const scroller = document.querySelector(".app-scroll");
    if (scroller) scroller.scrollTop = 0;
    // ליתר ביטחון גם את קונטיינר הכרטיסים (overflow-y: auto משלו)
    if (productsContainerRef.current) productsContainerRef.current.scrollTop = 0;
    window.scrollTo(0, 0); // fallback ללייאאוטים שבהם החלון כן נגלל
  };

  /* -------------------------------- */
  /* כשמשחררים האצבע -> מעבר תתי־קטגוריה/קטגוריה */
  /* -------------------------------- */
  const handleTouchEnd = () => {
    const totalCategories = allCategories.length;
    const subCats = all_sub_categories[activeCategoryIndex] || [];
    const totalSubCats = subCats.length;

    if (swipeDirection.current === "right") {
      // החלקה ימינה
      if (activeSubCategoryIndex > 0) {
        // יש תתי־קטגוריה קודמת => פשוט מפחיתים באחד
        setActiveSubCategoryIndex(activeSubCategoryIndex - 1);
        // אנימציה משולשת (כמו בקטגוריות)
        animateRight();
      } else {
        // wrap-around לקטגוריה הקודמת
        const prevIndex =
          (activeCategoryIndex - 1 + totalCategories) % totalCategories;
        setActiveCategoryIndex(prevIndex);

        // קובעים את תת־הקטגוריה האחרונה של הקטגוריה הקודמת
        const prevSub = all_sub_categories[prevIndex] || [];
        setActiveSubCategoryIndex(prevSub.length ? prevSub.length - 1 : 0);

        // אנימציה משולשת
        animateRight();
      }
      scrollToTop();
    } else if (swipeDirection.current === "left") {
      // החלקה שמאלה
      if (activeSubCategoryIndex < totalSubCats - 1) {
        // יש עוד תתי־קטגוריות => מוסיפים 1
        setActiveSubCategoryIndex(activeSubCategoryIndex + 1);
        // אנימציה
        animateLeft();
      } else {
        // wrap-around לקטגוריה הבאה
        const nextIndex = (activeCategoryIndex + 1) % totalCategories;
        setActiveCategoryIndex(nextIndex);
        setActiveSubCategoryIndex(0);

        // אנימציה
        animateLeft();
      }
      scrollToTop();
    } else {
      // לא הייתה החלקה
      setContainerStyle({});
    }
  };

  /* מעבר לעמוד מחיר */
  // const moveToPriceList = (barcode) => {
  //   nav(`/priceList/${barcode}`);
  // };

  const glassSquaresRef = useRef([]);

  /* הוספה לסל */
  const incrementAmount = (barcode) => {
    const newAmount = (productAmounts[barcode] || 0) + 1;
    const button = document.querySelector(`#add-to-cart-${barcode}`);
    setProductAmounts({ ...productAmounts, [barcode]: newAmount });

    // אנימציה למעלה
    setAnimatingProducts({ ...animatingProducts, [barcode]: "up" });
    setTimeout(() => {
      setAnimatingProducts((prev) => ({ ...prev, [barcode]: null }));
    }, 400);

    if (!oldProductAmounts[barcode]) {
      changeButtonToAddProductButton(button);
    } else {
      if (newAmount === 0) {
        changeButtonToDeleteProductButton(button);
      } else if (newAmount === oldProductAmounts[barcode]) {
        changeButtonToNoChangeAmountButton(button);
      } else {
        changeButtonToUpdateAmountButton(button);
      }
    }
  };

  /* הורדה מהסל */
  const decrementAmount = (barcode) => {
    const currentAmount = productAmounts[barcode] || 0;
    if (currentAmount === 0) return; // לא מפחיתים מתחת ל-0

    const newAmount = currentAmount - 1;
    const button = document.querySelector(`#add-to-cart-${barcode}`);
    setProductAmounts({ ...productAmounts, [barcode]: newAmount });

    // אנימציה למטה
    setAnimatingProducts({ ...animatingProducts, [barcode]: "down" });
    setTimeout(() => {
      setAnimatingProducts((prev) => ({ ...prev, [barcode]: null }));
    }, 400);

    if (!oldProductAmounts[barcode]) {
      if (newAmount === 0) {
        changeButtonToNoChangeAmountButton(button);
      } else {
        changeButtonToAddProductButton(button);
      }
    } else {
      if (newAmount === 0) {
        changeButtonToDeleteProductButton(button);
      } else if (newAmount === oldProductAmounts[barcode]) {
        changeButtonToNoChangeAmountButton(button);
      } else {
        changeButtonToUpdateAmountButton(button);
      }
    }
  };

  /* כשלוחצים על הכפתור (הוסף/עדכן/מחק) */
  const updateAmount = async (barcode) => {
    const amount = productAmounts[barcode] || 0;
    const button = document.querySelector(`#add-to-cart-${barcode}`);

    if (amount === 0) {
      // מחיקה
      remove(barcode);
      changeButtonToNoChangeAmountButton(button);
      // await deleteProductFromCart(userId, barcode);
      // await loadCart(userId);
      setOldProductAmounts({ ...oldProductAmounts, [barcode]: 0 });
      return;
    }

    // אם לא היה בסל - הוספה
    if (!oldProductAmounts[barcode]) {
      changeButtonToNoChangeAmountButton(button);
      add(barcode, amount);
      // await addProductToCart(userId, barcode, amount);
      // await loadCart(userId);
      setOldProductAmounts({ ...oldProductAmounts, [barcode]: amount });
      return;
    }

    // אחרת - עדכון
    changeButtonToNoChangeAmountButton(button);
    update(barcode, amount);
    // await updateProductInCart(userId, barcode, amount);
    // await loadCart(userId);
    setOldProductAmounts({ ...oldProductAmounts, [barcode]: amount });
  };

  /* תצוגת רשת — +/- מעדכנים את העגלה ישירות, בלי כפתור אישור */
  const directIncrement = (product) => {
    const current = product.amountInCart || 0;
    vibrate(120);
    setAnimatingProducts((prev) => ({ ...prev, [product.barcode]: "up" }));
    setTimeout(() => {
      setAnimatingProducts((prev) => ({ ...prev, [product.barcode]: null }));
    }, 400);
    if (current === 0) add(product.barcode, 1);
    else update(product.barcode, current + 1);
  };

  const directDecrement = (product) => {
    const current = product.amountInCart || 0;
    if (current <= 0) return;
    vibrate(120);
    setAnimatingProducts((prev) => ({ ...prev, [product.barcode]: "down" }));
    setTimeout(() => {
      setAnimatingProducts((prev) => ({ ...prev, [product.barcode]: null }));
    }, 400);
    if (current === 1) remove(product.barcode);
    else update(product.barcode, current - 1);
  };

  /* סינון מוצרים לפי קטגוריה ותת־קטגוריה פעילים */
  const currentCategory = allCategories[activeCategoryIndex];
  const subCats = all_sub_categories[activeCategoryIndex] || [];
  const currentSubCategory = subCats[activeSubCategoryIndex];

  // const filteredProducts = products.filter((product) => {
  const filteredProducts = productsWithDetails.filter((product) => {
    if (product.category !== currentCategory) return false;
    if (currentSubCategory) {
      return product.subcategory === currentSubCategory;
    }
    return true;
  });

  /* "הצג רק מוצרים הקיימים בכל הסניפים המועדפים" mode (toggled in Settings):
     when on + favorites chosen, keep only products carried by EVERY favorite
     supermarket. No-op (pass-through) otherwise. */
  const {
    products: availableProducts,
    isLoading: availabilityLoading,
    active: availabilityModeActive,
  } = useAvailableInFavorites(filteredProducts);

  // Push products without a price in the current supermarket to the bottom.
  // Array.prototype.sort is stable so available items keep their original order.
  const orderedProducts = [...availableProducts].sort(
    (a, b) => (a.unitPrice == null) - (b.unitPrice == null)
  );

  /* Shelf navigation (action #8): when a target is set, scroll to the first
     matching product card and flash it. Two triggers cover every case:
       • after-render effect — arriving here by navigation / subcategory change
       • store subscription — the user is ALREADY on the right subcategory,
         so no re-render would otherwise happen */
  const orderedRef = useRef([]);
  orderedRef.current = orderedProducts;

  const tryShelfScroll = useMemo(
    () => () => {
      const shelfTarget = peekShelfTarget();
      if (!shelfTarget) return;
      const hit = orderedRef.current.find((p) =>
        shelfTarget.barcodes.has(String(p.barcode))
      );
      if (!hit) return; // another subcategory rendered — try again next render
      consumeShelfTarget();
      requestAnimationFrame(() => {
        const card = document.getElementById(`product-card-${hit.barcode}`);
        if (!card) return;
        card.scrollIntoView({ behavior: "smooth", block: "center" });
        card.classList.add("shelf-target-flash");
        setTimeout(() => card.classList.remove("shelf-target-flash"), 2600);
      });
    },
    []
  );

  useEffect(() => {
    if (!isLoadingProducts) tryShelfScroll();
  });

  useEffect(
    () => onShelfTarget(() => setTimeout(tryShelfScroll, 60)),
    [tryShelfScroll]
  );

  /* Warm the image cache for the neighbouring shelves (next / previous
     subcategory) while the current one is on screen, so a swipe lands on
     pictures that are already in the browser cache. Runs when the browser is
     idle after a 2s quiet period (the shelf on screen gets the bandwidth
     first); the prefetch queue de-dupes per session and caps the batch. */
  useEffect(() => {
    if (isLoadingProducts) return undefined;
    const neighbours = [
      subCats[activeSubCategoryIndex + 1],
      subCats[activeSubCategoryIndex - 1],
    ].filter(Boolean);
    if (!neighbours.length) return undefined;
    return whenIdle(
      () => {
        const barcodes = neighbours.flatMap((sub) =>
          productsWithDetails
            .filter(
              (p) => p.category === currentCategory && p.subcategory === sub
            )
            .slice(0, 12)
            .map((p) => p.barcode)
        );
        prefetchProductImages(barcodes, { limit: 24 });
      },
      { minDelay: 2000, timeout: 3000 }
    );
    // productsWithDetails is read inside the idle callback on purpose — the
    // catalog itself never changes, only its cart/price decorations do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingProducts, currentCategory, activeSubCategoryIndex, subCats.length]);

  if (isLoadingProducts) {
    return (
      <div className={styles['spinner-container']}>
        <Spin size="large" />
        <p>טוען מוצרים...</p>
      </div>
    );
  }

  return (
    <>
      {/* Glass background squares - מחוץ לcontainer המונפש כדי שלא יושפע מדיפדוף */}
      <div className={styles['glass-bg']}>
        {(() => {
          if (!glassSquaresRef.current.length) {
            glassSquaresRef.current = Array.from({ length: 25 }, () => ({
              size: 40 + Math.random() * 60,
              left: Math.random() * 100,
              top: Math.random() * 100,
              duration: 20 + Math.random() * 20,
              delay: -Math.random() * 20,
            }));
          }
          return glassSquaresRef.current.map((sq, idx) => (
            <div
              key={idx}
              className={styles['glass-square']}
              style={{
                width: `${sq.size}px`,
                height: `${sq.size}px`,
                left: `${sq.left}%`,
                top: `${sq.top}%`,
                animationDuration: `${sq.duration}s`,
                animationDelay: `${sq.delay}s`,
              }}
            />
          ));
        })()}
      </div>

      <div
        className={[
          styles['list__product-list'],
          entering && styles['list__entering'],
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {/* ניווט הקטגוריות */}
        <CategoryNavigation />
        {/* ניווט תתי־קטגוריות */}
        <SubCategoryNavigation />

        <div className={styles['list__products-wrapper']}>
          <ProductComparisonModal
            isOpen={isComparisonModalOpen}
            onClose={closeComparisonModal}
          >
            <ProductComparison barcode={selectedBarcode} />
          </ProductComparisonModal>
          <div
            ref={productsContainerRef}
            className={styles['list__products-container']}
            style={containerStyle}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {availabilityModeActive && orderedProducts.length === 0 && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  padding: "52px 22px",
                  textAlign: "center",
                  color: "#64748b",
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                }}
              >
                {availabilityLoading
                  ? "בודק זמינות בסניפים המועדפים…"
                  : "אין מוצרים הקיימים בכל הסניפים המועדפים שברשימה"}
              </div>
            )}
            {orderedProducts.map((product, productIndex) => {
              const priceUnavailable = typeof product.unitPrice !== "number";
              return (
              <div
                id={`product-card-${product.barcode}`}
                className={`${styles['list__product-card']} ${
                  priceUnavailable ? styles['list__product-card--unavailable'] : ''
                }`}
                key={product.barcode}
                /* entrance stagger index (ProductsList.module.css) */
                style={{
                  "--i": Math.min(productIndex, ENTRANCE_STAGGER_MAX),
                }}
              >
                {product.discount && (
                  <div className={styles['list__product-badge']}>מבצע</div>
                )}

                <div className={styles['list__product-details']}>
                  <div className={styles['list__product-data']}>
                    <div className={styles['list__product-name']}>
                      <p>{product.name}</p>
                    </div>
                    <div className={styles['list__product-info']}>
                      <div className={styles['list__product-weight']}>
                        <p>{product.weight}</p>
                        <p>{convertWeightUnit(product.unitWeight)}</p>
                      </div>
                      <div className={styles['list__separator']}>|</div>
                      <div className={styles['list__product-brand']}>
                        <p>{product.brand}</p>
                      </div>
                    </div>
                    <div className={styles['list__product-price']}>
                      {typeof product.unitPrice === "number" ? (
                        <>
                          <p>{priceFormat(product.unitPrice)}</p>
                          <p>₪</p>
                        </>
                      ) : (
                        <p>מחיר לא זמין בסופר</p>
                      )}
                    </div>
                    {product.discount && discountPriceFormat(product.discount, styles)}
                  </div>
                  <div
                    className={styles['list__product-image']}
                    onClick={() => openComparisonModal(product.barcode)}
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    {/* first screenful loads eagerly (lazy would delay
                        above-the-fold pictures); the rest stay lazy */}
                    <ProductImageDisplay
                      barcode={product.barcode}
                      eager={productIndex < EAGER_IMAGE_COUNT}
                    />
                  </div>
                </div>
                <div className={styles['list__product-operations']}>
                  <div
                    id={`add-to-cart-${product.barcode}`}
                    className={styles['list__product-operations__confirm']}
                    onClick={(e) => {
                      e.stopPropagation();
                      updateAmount(product.barcode);
                    }}
                  >
                    אין שינוי
                  </div>
                  <div
                    className={`${styles['list__product-operations__add']} ${
                      animatingProducts[product.barcode] === "up"
                        ? styles['animate-bounce-plus']
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (layout === "grid") directIncrement(product);
                      else incrementAmount(product.barcode);
                    }}
                  >
                    <img src={plusIcon} alt="+" />
                  </div>
                  <div
                    className={`${styles['list__product-operations__quantity']} ${
                      animatingProducts[product.barcode] === "up"
                        ? styles['quantity-glow-up']
                        : animatingProducts[product.barcode] === "down"
                        ? styles['quantity-glow-down']
                        : ""
                    }`}
                  >
                    <span
                      className={
                        animatingProducts[product.barcode] === "up"
                          ? styles['animate-up']
                          : animatingProducts[product.barcode] === "down"
                          ? styles['animate-down']
                          : ""
                      }
                    >
                      {layout === "grid"
                        ? product.amountInCart || 0
                        : productAmounts[product.barcode] || 0}
                    </span>
                  </div>
                  <div
                    className={`${styles['list__product-operations__reduce']} ${
                      animatingProducts[product.barcode] === "down"
                        ? styles['animate-bounce-minus']
                        : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (layout === "grid") directDecrement(product);
                      else decrementAmount(product.barcode);
                    }}
                  >
                    <img src={minusIcon} alt="-" />
                  </div>
                  <div></div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

export default ProductsList;